import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import usePlaceNavigate from '../../hooks/usePlaceNavigate';
import { FLOWS_ENUM } from '../../common/enums';
import {
  selectConfigExpressApplyRentalStructureRequired,
  selectConfigExpressRegistrationTypeVerbiage,
  selectConfigExpressLocalContactVerbiage,
  selectConfigExpressParcelVerbiage,
  selectConfigExpressRenewLateFee,
  selectConfigExpressRenew,
} from '../../store/slices/config/configSelectors';
import {
  setCurrentStep,
  setStepCompletedStatus,
} from '../../store/slices/flow/flowSlice';
import { selectCurrentStep } from '../../store/slices/flow/flowSelectors';
import { updatePermitData } from '../../store/slices/renew/renewSlice';
import {
  selectDocumentUploadErrors,
  selectRenewalPermitData,
  selectRenewIsPaymentRequired,
  selectRenewLicenseError,
  selectRenewLicenseInProgress,
} from '../../store/slices/renew/renewSelectors';
import { renewLicense } from '../../store/slices/renew/renewThunks';
import { setSignatoryName } from '../../store/slices/payment/paymentSlice';
import { payNothingRenewal } from '../../store/slices/payment/paymentThunk';
import { selectCurrentUserEmail } from '../../store/slices/auth/authSelectors';
import { ConfirmationPage } from 'src/components/pages/ConfirmationPage';
import { RegistrationFlow } from 'src/types/configuration';
import { ApplicationData } from 'src/types/flow';
import { useAppDispatch } from 'src/store/store';
import LateRenewalNotice from 'src/components/renew/LateRenewalNotice';

const RenewConfirmation = () => {
  const placeNavigate = usePlaceNavigate();
  const dispatch = useAppDispatch();
  const configRenew = useSelector(selectConfigExpressRenew);

  const [hasPayNothingError, setHasPayNothingError] = useState(false);
  const [processingPayNothing, setProcessingPayNothing] = useState(false);

  const selectedEmailAddress = useSelector(selectCurrentUserEmail);

  const isPerformingRenew = useSelector(selectRenewLicenseInProgress);
  const renewLicenseError = useSelector(selectRenewLicenseError);
  const uploadDocumentsError = useSelector(selectDocumentUploadErrors);

  const selectedPermitApplicationData = useSelector(
    selectRenewalPermitData
  ) as ApplicationData;
  const selectedCurrentStep = useSelector(selectCurrentStep);
  const selectedRentalStructureRequiredStatus = useSelector(
    selectConfigExpressApplyRentalStructureRequired
  );
  const localContactVerbiage = useSelector(
    selectConfigExpressLocalContactVerbiage
  );
  const permitTypeVerbiage = useSelector(
    selectConfigExpressRegistrationTypeVerbiage
  );
  const parcelVerbiage = useSelector(selectConfigExpressParcelVerbiage);
  const selectedPaymentRequired = useSelector(selectRenewIsPaymentRequired);
  const lateRenewalFee = useSelector(selectConfigExpressRenewLateFee);

  const flowError = useMemo(() => {
    if (renewLicenseError) {
      return renewLicenseError;
    } else if (hasPayNothingError) {
      return 'Something went wrong while processing your request.';
    }
    return '';
  }, [renewLicenseError, hasPayNothingError]);

  const finalizeOnNext = (
    wasSuccessfull: boolean,
    requiresPayment: boolean
  ) => {
    // If successfull go to the success page, else error messages will be displayed
    if (wasSuccessfull) {
      if (requiresPayment) {
        dispatch(
          setStepCompletedStatus({ stepNum: selectedCurrentStep, status: true })
        );
        dispatch(setCurrentStep(selectedCurrentStep + 1));
        placeNavigate('/renew-payment');
      } else {
        setProcessingPayNothing(true);
        dispatch(
          payNothingRenewal(selectedEmailAddress ?? '', onPayNothingCompleted)
        );
      }
    }
  };

  const onPayNothingCompleted = (successfull: boolean = false) => {
    setHasPayNothingError(successfull);
    setProcessingPayNothing(false);
    if (successfull) {
      placeNavigate('/renew-success');
    }
  };

  const onConfirmation = (data: ApplicationData) => {
    dispatch(updatePermitData(data));
    dispatch(renewLicense(finalizeOnNext));
    dispatch(setSignatoryName(data.certifications?.signatory as string));
  };

  const instructionsText = useMemo(() => {
    return selectedPaymentRequired
      ? 'Please confirm the information below to proceed to the payment step.'
      : 'Please confirm the following information and sign below.';
  }, [selectedPaymentRequired]);

  return (
    <ConfirmationPage
      config={configRenew as RegistrationFlow}
      customNotice={lateRenewalFee && <LateRenewalNotice />}
      data={selectedPermitApplicationData}
      documentUploadErrors={uploadDocumentsError}
      flow={FLOWS_ENUM.RENEW_LICENSE}
      flowError={flowError}
      instructionsText={instructionsText}
      loading={processingPayNothing || isPerformingRenew}
      localContactVerbiage={localContactVerbiage}
      onConfirmation={onConfirmation}
      parcelVerbiage={parcelVerbiage}
      permitTypeVerbiage={permitTypeVerbiage}
      rentalStructureRequiredStatus={selectedRentalStructureRequiredStatus}
    />
  );
};

export default RenewConfirmation;
